/**
 * The call panel (call-center B2): on every call this browser is on (answered, or a call out from "Calling…" on), a panel
 * along the left of the page — a column beside the page on a wide screen, the whole screen on a phone or tablet, which
 * Minimize folds to a bar. At the top: who it is (the guest the number or a note's email finds, else the note's name,
 * else the number), the event, how long the call has been on, and Mute, Hold, Book and Hang up. Hold plays the hold music
 * to the other person and, here, mutes the microphone and the call's sound until Resume. Below, four tabs: Caller (each
 * booking found, with its balance, and every call the event has had with the number), Notes (the call's notes, saved as in
 * the notes popup), Rooms (the event's hotels and room types as guests see them, to read from) and Ask (what the caller
 * asked, answered by the assistant as a script to read out, and sent to the event's managers). When the call ends the
 * panel stays, so the notes can be finished; Close saves them first.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { InfoTip } from '@/components/ui/InfoTip';
import { InlineReport } from '@/components/ui/ReportControl';
import { getPublicEvent, hotelCardsOf, type PublicHotelCard, type PublicRoomType } from '@/features/public/api';
import { cn } from '@/lib/cn';
import { date, dateRange, dateTime, money, phone } from '@/lib/format';
import { AskTab } from './AskTab';
import { CallNotesDialog, type NotesControl } from './CallNotesDialog';
import {
  CALLER_FAILED, HISTORY_FAILED, HOLD_FAILED, OUTCOME_LABEL, callInfo, callOutcome, callerProfile, holdCall, numberCalls,
  type CallerPerson, type CallerProfile, type CallerProspect, type CallRow,
} from './data';
import type { PhoneCall } from './phone';

/** The call on the panel. `call` is null once it has ended; `id` is our own record's id (null for a call without one). */
export interface PanelCall {
  call: PhoneCall | null; id: string | null; out: boolean; talking: boolean; ended: boolean;
  /** What the call itself says: the number and, for a call that rang in, the name the network gave. */
  number: string | null; name: string | null;
}

type Tab = 'caller' | 'notes' | 'rooms' | 'ask';
/** Tags as chips: trimmed, blanks dropped, each once whatever its case (the first spelling stays). */
function distinct(tags: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const t = raw.trim();
    if (!t || seen.has(t.toLowerCase())) continue;
    seen.add(t.toLowerCase());
    out.push(t);
  }
  return out;
}

const STATUS: Record<CallerPerson['status'], { label: string; tone: 'success' | 'neutral' | 'warning' }> = {
  booked: { label: 'Booked', tone: 'success' }, cancelled: { label: 'Cancelled', tone: 'neutral' }, waitlist: { label: 'Waiting list', tone: 'warning' },
};

export function CallPanel({ pc, audio, onHangup, onClose, onBook }: {
  pc: PanelCall; audio: HTMLAudioElement | null; onHangup(): void; onClose(): void; onBook(id: string): void;
}) {
  const id = pc.id;
  const info = useQuery({ queryKey: ['call-info', id], queryFn: () => callInfo(id!), enabled: id !== null, staleTime: 60_000 });
  const own = id === null || pc.out; // what the call itself says is the other party only for a call without our record, or our own call out
  const number = info.data?.number ?? (own ? pc.number : null);
  const eventId = info.data?.event_id ?? null;
  const caller = useQuery({ queryKey: ['caller', id], queryFn: () => callerProfile(id!), enabled: id !== null, staleTime: 30_000, retry: 1 });
  const notesRef = useRef<NotesControl | null>(null);
  const [tab, setTab] = useState<Tab>('caller');
  const [small, setSmall] = useState(false); // folded to a bar (below 1366 px)
  const barRef = useRef<HTMLDivElement | null>(null);
  // the folded bar sits over the bottom of the page: the page keeps that much room at its end, so nothing hides under it
  useEffect(() => {
    const bar = barRef.current;
    if (!small || pc.ended || !bar) return;
    const root = document.documentElement;
    const room = () => root.style.setProperty('--call-bar-h', `${Math.ceil(bar.getBoundingClientRect().height)}px`);
    room();
    const seen = new ResizeObserver(room);
    seen.observe(bar);
    return () => { seen.disconnect(); root.style.removeProperty('--call-bar-h'); };
  }, [small, pc.ended]);
  const [closing, setClosing] = useState(false);

  // when the call was answered: a call in, when it went through here; a call out, when our record says the number answered
  const [answeredAt, setAnsweredAt] = useState<number | null>(null);
  useEffect(() => { if (!pc.out && pc.talking && answeredAt === null) setAnsweredAt(Date.now()); }, [pc.out, pc.talking, answeredAt]);
  useEffect(() => {
    if (!pc.out || !id || answeredAt !== null || pc.ended) return;
    let live = true;
    const look = () => void callOutcome(id).then((o) => { if (live && o === 'answered') setAnsweredAt(Date.now()); }, () => undefined);
    look();
    const t = window.setInterval(look, 2000);
    return () => { live = false; window.clearInterval(t); };
  }, [pc.out, id, answeredAt, pc.ended]);
  const [endedAt, setEndedAt] = useState<number | null>(null);
  useEffect(() => { if (pc.ended && endedAt === null) setEndedAt(Date.now()); }, [pc.ended, endedAt]);
  const [, tick] = useState(0);
  useEffect(() => {
    if (answeredAt === null || pc.ended) return;
    const t = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [answeredAt, pc.ended]);
  const secs = answeredAt === null ? 0 : Math.max(0, Math.floor(((endedAt ?? Date.now()) - answeredAt) / 1000));
  const clock = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

  // Mute is the person's own choice; Hold mutes too, and silences the call here, until Resume puts back what Mute had
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [holding, setHolding] = useState(false);
  const [holdError, setHoldError] = useState<string | null>(null);
  const presses = useRef(0);
  // the panel going away (the next call answered, the line dropped) keeps what was typed: unsaved notes are saved first
  const closed = useRef(false); // Close saved (or is saving) first: the unmount save stands down
  useEffect(() => () => { const n = notesRef.current; if (!closed.current && n?.dirty) void n.save(); }, []);
  const sound = (on: boolean) => { if (audio) audio.muted = !on; };
  const mute = () => { const m = !muted; setMuted(m); if (!held) pc.call?.mute(m); };
  const hold = async () => {
    if (!id || holding) return;
    const next = !held;
    setHolding(true);
    setHoldError(null);
    if (next) { pc.call?.mute(true); sound(false); }
    // a press number never used before on this call, even by an earlier panel for it: the provider takes a repeated one as a resend
    presses.current = Math.max(presses.current + 1, Date.now());
    const r = await holdCall(id, next, presses.current);
    setHolding(false);
    if (!r.ok) {
      // nothing changed for the other person: put this side back as it was
      if (next) { pc.call?.mute(muted); sound(true); }
      setHoldError(r.message || HOLD_FAILED);
      return;
    }
    if (!next) { pc.call?.mute(muted); sound(true); }
    setHeld(next);
  };
  // a call that ends while held leaves the sound on for the next one
  useEffect(() => { if (pc.ended) sound(true); });

  const answered = answeredAt !== null && !pc.ended;
  const person = caller.data?.people[0] ?? null;
  const prospect = caller.data?.prospects[0] ?? null;
  // the flag and the tags a host put on a prospect with this number (G2): each distinct one once, flags first, six at most
  const flags = [...new Set((caller.data?.prospects ?? []).map((x) => x.flag?.trim()).filter((f): f is string => Boolean(f)))];
  const seenChip = new Set(flags.map((f) => f.toLowerCase()));
  const tags: string[] = [];
  for (const t of distinct((caller.data?.prospects ?? []).flatMap((x) => x.tags ?? []))) {
    if (seenChip.has(t.toLowerCase())) continue;
    seenChip.add(t.toLowerCase());
    tags.push(t);
  }
  const chips = [...flags.map((text) => ({ text, flag: true })), ...tags.map((text) => ({ text, flag: false }))];
  const MAX_CHIPS = 6;
  // the same read as the Caller tab's call list (shared): the name a note gave this number names the caller when no booking does
  const history = useQuery({ queryKey: ['number-calls', eventId, number], queryFn: () => numberCalls(eventId!, number!), enabled: Boolean(eventId && number), staleTime: 15_000 });
  const noteName = history.data?.find((c) => c.note?.name?.trim())?.note?.name?.trim() || null;
  // the phone's own name for a call put through from the line is the line's, so it names only a call without our record
  const title = person?.name ?? prospect?.name ?? noteName ?? (own ? pc.name : null) ?? (number ? phone(number) : id && info.isPending ? 'Finding the caller…' : 'Unknown caller');
  const state = pc.ended ? 'Call ended' : held ? 'On hold' : answered ? 'On a call' : pc.out ? (pc.talking ? 'Ringing…' : 'Calling…') : 'Connecting…';

  const close = async () => {
    if (closing) return;
    const n = notesRef.current;
    closed.current = true;
    if (n?.dirty) {
      setClosing(true);
      const ok = await n.save();
      setClosing(false);
      if (!ok) { closed.current = false; setTab('notes'); return; }
    }
    onClose();
  };

  if (small && !pc.ended) {
    return (
      <div ref={barRef} role="region" aria-label="Call" data-testid="call-panel" data-small="true"
        className="fixed inset-x-0 bottom-0 z-toast flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line bg-popover px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] elevate">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink">{title}</p>
          <p className="text-sm text-ink-2" role="status" data-testid="call-out-state">{state}{answeredAt !== null && <> · <span className="tabular-nums">{clock}</span></>}</p>
        </div>
        <Button className="max-sm:min-h-11" onClick={() => setSmall(false)} data-testid="call-panel-open">Open</Button>
        <Button variant="danger" className="max-sm:min-h-11" onClick={onHangup} data-testid="call-hangup">Hang up</Button>
      </div>
    );
  }

  return (
    <aside aria-label="Call" data-testid="call-panel"
      className={'fixed inset-0 z-overlay flex flex-col overflow-hidden bg-popover animate-rise lg:w-[400px] lg:animate-none lg:border-r '
        // 1024–1365 px: over the page beside the nav, so the page keeps its width (a range, so none of it reaches 1366 px)
        + 'lg:max-[1366px]:absolute lg:max-[1366px]:inset-y-0 lg:max-[1366px]:left-0 lg:max-[1366px]:right-auto lg:max-[1366px]:border-line-strong '
        // from 1366 px a column beside it
        + 'min-[1366px]:static min-[1366px]:z-auto min-[1366px]:shrink-0 min-[1366px]:border-line'}>
      <div className="border-b border-line px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h2 className="min-w-0 break-words text-base font-semibold text-ink" data-testid="call-panel-title">{title}</h2>
              {chips.slice(0, MAX_CHIPS).map((c) => (
                <Badge key={`${c.flag ? 'f' : 't'}:${c.text}`} tone={c.flag ? 'warning' : 'neutral'} className="max-w-full truncate" data-testid={c.flag ? 'call-panel-flag' : 'call-panel-tag'}>{c.text}</Badge>
              ))}
              {chips.length > MAX_CHIPS && (
                <Badge tone="neutral" title={chips.slice(MAX_CHIPS).map((c) => c.text).join(', ')} data-testid="call-panel-more">+{chips.length - MAX_CHIPS}</Badge>
              )}
            </div>
            <p className="break-words text-sm text-ink-2 tabular-nums" data-testid="call-out-number">
              {[number ? phone(number) : null, info.data?.event].filter(Boolean).join(' · ') || (id ? '' : 'Unknown caller')}
            </p>
            <p className="mt-1 text-sm font-semibold text-ink" role="status" data-testid="call-out-state">
              {state}{answeredAt !== null && <> · <span className="tabular-nums">{clock}</span></>}
            </p>
          </div>
          {!pc.ended && <Button variant="ghost" className="max-sm:min-h-11 min-[1366px]:hidden" onClick={() => setSmall(true)} data-testid="call-panel-min">Minimize</Button>}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {pc.ended ? (
            <Button variant="primary" className="max-sm:min-h-11" busy={closing} busyLabel="Saving…" onClick={() => void close()} data-testid="call-panel-close">Close</Button>
          ) : (
            <>
              {answered && <Button className="max-sm:min-h-11" aria-pressed={muted} disabled={held} onClick={mute} data-testid="call-mute">{muted ? 'Unmute' : 'Mute'}</Button>}
              {answered && id && (
                <Button className="max-sm:min-h-11" aria-pressed={held} busy={holding} busyLabel={held ? 'Resuming…' : 'Holding…'} onClick={() => void hold()} data-testid="call-hold">
                  {held ? 'Resume' : 'Hold'}
                </Button>
              )}
              {id && <Button className="max-sm:min-h-11" onClick={() => onBook(id)} data-testid="call-book-open">Book</Button>}
              <Button variant="danger" className="max-sm:min-h-11" onClick={onHangup} data-testid="call-hangup">Hang up</Button>
            </>
          )}
          {answered && id && (
            <InfoTip title="Hold" align="end">
              Hold plays music to the other person and mutes your microphone until you press Resume. The call’s recording pauses while they are on hold.
            </InfoTip>
          )}
        </div>
        {holdError && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <p role="alert" className="text-st-danger" data-testid="call-hold-error">{holdError}</p>
            <InlineReport message={holdError} />
          </div>
        )}
      </div>

      {id ? (
        <>
          <div className="flex gap-1 border-b border-line px-4 py-2" role="tablist" aria-label="Call details">
            {(['caller', 'notes', 'rooms', 'ask'] as const).map((t) => (
              <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`call-tab-${t}`}
                className={cn('rounded-chip px-3 py-1 text-sm ring-1 max-sm:min-h-11', tab === t ? 'bg-ink text-canvas ring-ink' : 'bg-surface text-ink-2 ring-line')}>
                {t === 'caller' ? 'Caller' : t === 'notes' ? 'Notes' : t === 'rooms' ? 'Rooms' : 'Ask'}
              </button>
            ))}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            {tab === 'caller' && <CallerTab q={caller} eventId={eventId} number={number} />}
            {/* kept mounted, so typed notes survive a look at another tab */}
            <div hidden={tab !== 'notes'}>
              <CallNotesDialog callId={id} open inline control={notesRef} onClose={() => undefined} />
            </div>
            {tab === 'rooms' && <RoomsTab eventId={eventId} />}
            {/* kept mounted too, so a typed question and this call's answers survive a look at another tab */}
            <div hidden={tab !== 'ask'}>
              <AskTab key={id} callId={id} eventId={eventId} />
            </div>
          </div>
        </>
      ) : (
        <p className="px-4 py-4 text-sm text-ink-2">This call didn’t come through an event line, so there is nothing more to show.</p>
      )}
    </aside>
  );
}

function CallerTab({ q, eventId, number }: { q: ReturnType<typeof useQuery<CallerProfile>>; eventId: string | null; number: string | null }) {
  const history = useQuery({ queryKey: ['number-calls', eventId, number], queryFn: () => numberCalls(eventId!, number!), enabled: Boolean(eventId && number), staleTime: 15_000 });
  const prospects = !!q.data?.prospects.length && (
    <section aria-label="On the prospects list" className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-ink">On the prospects list</h3>
      {q.data.prospects.map((p) => <ProspectCard key={p.id} p={p} />)}
    </section>
  );
  // a prospect with no booking: who they are comes first, then a short "not booked yet"
  const prospectOnly = !!q.data && !q.data.people.length && !!q.data.prospects.length;
  return (
    <div className="flex flex-col gap-5" data-testid="call-caller">
      {prospectOnly && prospects}
      <section aria-label="Bookings" className="flex flex-col gap-3">
        {q.isLoading ? <p className="text-sm text-ink-2">Looking up this caller…</p> : q.isError ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <p role="alert" className="text-st-danger" data-testid="call-caller-error">{CALLER_FAILED}</p>
            <InlineReport message={CALLER_FAILED} />
          </div>
        ) : prospectOnly ? (
          <p className="text-sm text-ink-2" data-testid="call-caller-not-booked">Not booked yet.</p>
        ) : !q.data?.people.length ? (
          <p className="text-sm text-ink-2" data-testid="call-caller-none">No booking found for this number. Ask for the name or email on the booking, then search the guest list.</p>
        ) : q.data.people.map((p) => <PersonCard key={p.id} p={p} />)}
      </section>
      {!prospectOnly && prospects}
      <section aria-label="Calls with this number" className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-ink">Calls with this number</h3>
        {history.isLoading ? <p className="text-sm text-ink-2">Loading…</p> : history.isError ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <p role="alert" className="text-st-danger">{HISTORY_FAILED}</p>
            <InlineReport message={HISTORY_FAILED} />
          </div>
        ) : !history.data?.length ? <p className="text-sm text-ink-2">No other calls.</p> : (
          <ul className="divide-y divide-line rounded-card border border-line" data-testid="call-history">
            {history.data.map((c) => <HistoryRow key={c.id} c={c} />)}
          </ul>
        )}
      </section>
    </div>
  );
}

function ProspectCard({ p }: { p: CallerProspect }) {
  return (
    <article className="rounded-card border border-line p-3 text-sm" data-testid="call-prospect">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 break-words font-semibold text-ink">{p.name}</p>
        {p.flag && <Badge tone="warning" className="max-w-full truncate">{p.flag}</Badge>}
      </div>
      {!!distinct(p.tags).length && (
        <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Tags" data-testid="call-prospect-tags">
          {distinct(p.tags).map((t) => <li key={t} className="max-w-full truncate rounded-chip bg-surface-2 px-2.5 py-0.5 text-[13px] text-ink ring-1 ring-line">{t}</li>)}
        </ul>
      )}
      {p.note && <p className="mt-2 whitespace-pre-line break-words text-ink-2" data-testid="call-prospect-note">{p.note}</p>}
    </article>
  );
}

function PersonCard({ p }: { p: CallerPerson }) {
  const s = STATUS[p.status];
  const rows: [string, string | null][] = [
    ['Confirmation #', p.confirmation_number], ['Room', p.room_type], ['Dates', p.check_in || p.check_out ? dateRange(p.check_in, p.check_out) : null],
    ['Hotel', p.hotel ? (p.hotel_confirmation ? `${p.hotel} · hotel # ${p.hotel_confirmation}` : p.hotel) : null],
    ['In the room', p.party.length ? p.party.join(', ') : null], ['Email', p.email], ['Requests', p.special_requests], ['Booked', date(p.booked_at)],
  ];
  return (
    <article className="rounded-card border border-line p-3 text-sm" data-testid="call-person">
      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 break-words font-semibold text-ink">{p.name}</p>
        <Badge tone={s.tone}>{s.label}</Badge>
      </div>
      {p.matched_by === 'email' && <p className="mt-1 text-ink-2">Found by the email on a call note.</p>}
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="contents"><dt className="text-ink-2">{k}</dt><dd className="min-w-0 break-words text-ink">{v}</dd></div>
        ))}
      </dl>
      {p.invoice && (
        <div className="mt-3 grid grid-cols-3 gap-2 rounded-control bg-surface-2 p-2 text-center" data-testid="call-person-invoice">
          <div><p className="text-[12px] text-ink-2">Total</p><p className="font-semibold tabular-nums text-ink">{money(p.invoice.total_cents)}</p></div>
          <div><p className="text-[12px] text-ink-2">Paid</p><p className="font-semibold tabular-nums text-ink">{money(p.invoice.paid_cents)}</p></div>
          <div><p className="text-[12px] text-ink-2">Balance</p><p className={cn('font-semibold tabular-nums', p.invoice.balance_cents > 0 ? 'text-st-danger' : 'text-ink')}>{money(p.invoice.balance_cents)}</p></div>
        </div>
      )}
    </article>
  );
}

function HistoryRow({ c }: { c: CallRow }) {
  const summary = c.voicemail?.recap ?? c.voicemail?.recap_callback ?? null;
  return (
    <li className="px-3 py-2 text-sm">
      <p className="text-ink"><span className="font-semibold">{c.direction === 'in' ? 'In' : 'Out'}</span> · <span className="tabular-nums">{dateTime(c.started_at)}</span> · {OUTCOME_LABEL[c.outcome] ?? c.outcome}</p>
      {c.who && <p className="mt-1 text-ink-2">{c.direction === 'in' ? 'Answered by' : 'Called by'} {c.who}</p>}
      {c.note?.notes && <p className="mt-1 whitespace-pre-line break-words text-ink-2">{c.note.notes}</p>}
      {summary && <p className="mt-1 break-words text-ink-2">{summary}</p>}
    </li>
  );
}

/** A room type's price a night, as a range over the nights that have one; null when nothing is priced. */
export function nightRange(t: PublicRoomType): string | null {
  const ps = Object.values(t.nights ?? {}).map((n) => n.p).filter((p): p is number => typeof p === 'number');
  if (!ps.length) return null;
  const lo = Math.min(...ps);
  const hi = Math.max(...ps);
  return lo === hi ? `${money(lo)} a night` : `${money(lo)}–${money(hi)} a night`;
}

function RoomsTab({ eventId }: { eventId: string | null }) {
  const ev = useQuery({ queryKey: ['public-event', eventId], queryFn: () => getPublicEvent(eventId!), enabled: eventId !== null, staleTime: 60_000, retry: 1 });
  if (!eventId || ev.isLoading) return <p className="text-sm text-ink-2">Loading…</p>;
  // the rooms as guests see them: an event still being set up, with its booking page not open, has none to show yet
  if (ev.isSuccess && !ev.data) return <p className="text-sm text-ink-2" data-testid="call-rooms-none">The rooms show here once the event’s booking page is open.</p>;
  if (ev.isError || !ev.data) {
    const msg = 'Couldn’t load the rooms — try again.';
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <p role="alert" className="text-st-danger">{msg}</p>
        <InlineReport message={msg} />
      </div>
    );
  }
  const hotels = hotelCardsOf(ev.data);
  if (!hotels.length) return <p className="text-sm text-ink-2">This event has no hotel rooms set up yet.</p>;
  return <div className="flex flex-col gap-5" data-testid="call-rooms">{hotels.map((h) => <HotelBlock key={h.id} h={h} />)}</div>;
}

function HotelBlock({ h }: { h: PublicHotelCard }) {
  const times = [h.check_in_time ? `Check-in ${h.check_in_time}` : null, h.check_out_time ? `check-out ${h.check_out_time}` : null].filter(Boolean).join(', ');
  return (
    <section aria-label={h.hotel_name} className="flex flex-col gap-2 text-sm">
      <div>
        <h3 className="font-semibold text-ink">{h.hotel_name}</h3>
        {h.address && <p className="break-words text-ink-2">{h.address}</p>}
        {times && <p className="text-ink-2">{times}</p>}
        {h.description && <p className="mt-1 whitespace-pre-line break-words text-ink">{h.description}</p>}
      </div>
      <ul className="flex flex-col gap-2">
        {h.room_types.map((t) => {
          const price = nightRange(t);
          const beds = t.beds ? `Sleeps ${t.beds.max_people}${t.beds.sofa_beds ? ` · ${t.beds.sofa_beds} sofa bed${t.beds.sofa_beds === 1 ? '' : 's'}` : ''}` : null;
          return (
            <li key={t.type} className="rounded-card border border-line p-3" data-testid="call-room-type">
              <div className="flex flex-wrap items-center gap-2">
                <p className="min-w-0 flex-1 break-words font-semibold text-ink">{t.label}</p>
                {t.closed && <Badge tone="neutral">Not on offer</Badge>}
              </div>
              {(price || beds) && <p className="mt-1 text-ink-2">{[beds, price].filter(Boolean).join(' · ')}</p>}
              {t.description ? <p className="mt-1 whitespace-pre-line break-words text-ink">{t.description}</p> : <p className="mt-1 text-ink-2">No description yet.</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
